import { useLayoutEffect, useRef, ComponentProps } from 'react';
import { noop } from 'lodash';

import { IonInput } from '@ionic/react';
import { useContextSelector } from 'use-context-selector';

import { FormContext } from '../../../contexts/FormContext';

import { FormTabIndexProviderContext } from './FormTabIndexProvider.context';

interface UseFormContolTabIndexParams {
  tabIndex?: number;
  enterKeyHint?: ComponentProps<typeof IonInput>['enterkeyhint'];
}

export const useFormContolTabIndex = ({
  tabIndex,
  enterKeyHint = 'next',
}: UseFormContolTabIndexParams) => {
  const ref = useRef<HTMLIonInputElement>();

  const setIndex = useContextSelector(
    FormTabIndexProviderContext,
    ({ setIndex }) => setIndex
  );

  const removeIndex = useContextSelector(
    FormTabIndexProviderContext,
    ({ removeIndex }) => removeIndex
  );

  const focusNext = useContextSelector(
    FormTabIndexProviderContext,
    ({ focusNext }) => focusNext
  );

  const submit = useContextSelector(FormContext, ({ submit }) => submit);

  useLayoutEffect(() => {
    if (tabIndex) {
      const control = ref.current;
      const listener = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (enterKeyHint === 'next') focusNext(tabIndex);
          if (enterKeyHint === 'go') submit();
        }
      };
      control?.addEventListener('keydown', listener);
      return () => control?.removeEventListener('keydown', listener);
    }
    return noop;
  }, [enterKeyHint, tabIndex, focusNext, submit]);

  useLayoutEffect(() => {
    if (tabIndex) {
      setIndex(tabIndex, ref);
      return () => removeIndex(tabIndex);
    }
    return noop;
  }, [tabIndex, setIndex, removeIndex]);

  return { ref, enterKeyHint };
};
